import { useEffect, useId } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Check, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface Props {
  abierto: boolean;
  alCerrar: () => void;
  titulo: string;
  descripcion: string;
  icono: LucideIcon;

  alEnviar?: ((evento: FormEvent) => void) | undefined;
  enviando?: boolean | undefined;
  children: ReactNode;
}

export function PanelLateral({
  abierto,
  alCerrar,
  titulo,
  descripcion,
  icono: Icono,
  alEnviar,
  enviando = false,
  children,
}: Props) {
  const idTitulo = useId();

  useEffect(() => {
    if (!abierto) {
      return;
    }
    function alPulsar(evento: KeyboardEvent) {
      if (evento.key === 'Escape') {
        alCerrar();
      }
    }
    document.addEventListener('keydown', alPulsar);
    return () => document.removeEventListener('keydown', alPulsar);
  }, [abierto, alCerrar]);

  useEffect(() => {
    if (!abierto) {
      return;
    }
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previo;
    };
  }, [abierto]);

  if (!abierto) {
    return null;
  }

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-armazon-900/40 backdrop-blur-[2px]"
        onClick={alCerrar}
        aria-hidden
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="fixed inset-y-0 right-0 z-50 flex w-[min(520px,100vw)] flex-col bg-white entrar-derecha shadow-2xl"
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-armazon-100 px-5 py-4">
          <span className="grid size-[42px] shrink-0 place-items-center rounded-lg bg-marca-50 text-marca-700">
            <Icono className="size-5" aria-hidden />
          </span>

          <div className="mr-auto min-w-0">
            <h2 id={idTitulo} className="text-base font-extrabold uppercase tracking-wide text-armazon-900">
              {titulo}
            </h2>
            <p className="mt-0.5 text-xs font-medium uppercase tracking-[0.06em] text-armazon-500">
              {descripcion}
            </p>
          </div>

          <button
            type="button"
            onClick={alCerrar}
            aria-label="Cerrar"
            title="Cerrar"
            className="grid size-8 shrink-0 place-items-center rounded-md text-armazon-500 transition-colors hover:bg-armazon-100 hover:text-armazon-800"
          >
            <X className="size-[18px]" aria-hidden />
          </button>
        </header>

        <form
          onSubmit={(evento) => {
            evento.preventDefault();
            alEnviar?.(evento);
          }}
          noValidate
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="grid min-h-0 flex-1 content-start gap-4 overflow-y-auto p-5">
            {children}
          </div>

          {alEnviar && (
          <footer className="flex shrink-0 justify-end gap-2 border-t border-armazon-100 bg-white px-5 py-4">
            <button
              type="button"
              onClick={alCerrar}
              disabled={enviando}
              title="Cancelar"
              aria-label="Cancelar"
              className="grid size-11 place-items-center rounded-lg border border-armazon-200 bg-white text-armazon-500 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-alerta disabled:cursor-not-allowed disabled:opacity-55"
            >
              <X className="size-5" aria-hidden />
            </button>

            <button
              type="submit"
              disabled={enviando}
              title="Guardar"
              aria-label="Guardar"
              className="grid size-11 place-items-center rounded-lg bg-marca-600 text-white transition-colors hover:bg-marca-700 disabled:cursor-not-allowed disabled:opacity-55"
            >
              <Check className="size-5" aria-hidden />
            </button>
          </footer>
          )}
        </form>
      </aside>
    </>
  );
}

export function DosColumnas({ children }: { children: ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2">{children}</div>;
}

export function CampoDelPanel({
  etiqueta,
  error,
  ayuda,
  requerido = false,
  children,
}: {
  etiqueta: string;
  error?: string | undefined;
  ayuda?: string | undefined;
  requerido?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="grid content-start gap-2">
      <span className="text-sm font-semibold uppercase text-armazon-900">
        {etiqueta}
        {requerido && <span className="ml-0.5 text-alerta">*</span>}
      </span>
      {children}
      {error ? (
        <span role="alert" className="text-sm text-alerta">
          {error}
        </span>
      ) : (
        ayuda && (
          <span className="text-xs uppercase tracking-[0.03em] text-armazon-500">{ayuda}</span>
        )
      )}
    </label>
  );
}

export const claseEntradaPanel =
  'h-11 w-full rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-900 outline-none transition-colors placeholder:text-armazon-400 focus:border-marca-400 focus:ring-2 focus:ring-marca-500/20';
